import { pessoaService } from "@/services/api/PessoaService";
import { PessoaDTO } from "@/types/pessoa";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from "react";
import { AUTH_SESSION_KEY, getStoredUser, removeStoredUser, setStoredUser } from "./auth-helpers";
import { SessionState, sessionReducer } from "./auth/session";
import { useCrossTabSync } from "./auth/use-cross-tab-sync";
import { useRehydrateUser } from "./auth/use-rehydrate-user";

interface AuthContextProps {
  user: PessoaDTO | null;
  isAuthenticated: boolean;
  isRehydrating: boolean;
  login: (user: PessoaDTO) => void;
  /** Atualiza campos locais do usuário (ex.: foto) sem ir ao servidor. */
  updateUser: (patch: Partial<PessoaDTO>) => void;
  /** Revalida o usuário contra o servidor (GET /me) sob demanda. */
  refreshUser: () => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

/** Lê a sessão inicial do localStorage (snapshot otimista do último login). */
function readInitialSession(): SessionState {
  return { user: getStoredUser() };
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, dispatch] = useReducer(sessionReducer, undefined, readInitialSession);
  const isAuthenticated = !!session.user;
  const [isRehydrating, setIsRehydrating] = useState(isAuthenticated);

  // --- Operações da sessão (contrato público) ---------------------------------

  const login = useCallback((user: PessoaDTO) => {
    dispatch({ type: "login", user });
  }, []);

  const logout = useCallback(() => {
    removeStoredUser();
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem(AUTH_SESSION_KEY);
    dispatch({ type: "logout" });
    window.location.href = "/login";
  }, []);

  const updateUser = useCallback((patch: Partial<PessoaDTO>) => {
    dispatch({ type: "patchUser", patch });
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const pessoa = await pessoaService.getMe();
      dispatch({ type: "replaceUser", user: pessoa });
    } catch {
      // Falha de rede não derruba a sessão; mantém o estado atual.
    }
  }, []);

  // --- Persistência: localStorage como projeção do estado ---------------------

  useEffect(() => {
    if (session.user) setStoredUser(session.user);
  }, [session.user]);

  // --- Efeitos de sessão ------------------------------------------------------

  const handleRehydrated = useCallback((user: PessoaDTO) => {
    dispatch({ type: "replaceUser", user });
  }, []);

  const handleRehydrationSettled = useCallback(() => setIsRehydrating(false), []);

  const handleStorageSync = useCallback(() => {
    dispatch({ type: "sync", user: getStoredUser() });
  }, []);

  useRehydrateUser({
    enabled: isAuthenticated,
    onUser: handleRehydrated,
    onSettled: handleRehydrationSettled,
  });
  useCrossTabSync(handleStorageSync);

  const value = useMemo<AuthContextProps>(
    () => ({
      user: session.user,
      isAuthenticated,
      isRehydrating,
      login,
      updateUser,
      refreshUser,
      logout,
    }),
    [session.user, isAuthenticated, isRehydrating, login, updateUser, refreshUser, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
